import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import AdminLayout from '../components/AdminLayout';
import TicketTable from '../components/tickets/TicketTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle,
  faCalendarAlt,
  faChevronDown,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import '../styles/FinancialsPage.css';
import groupService from '../api/groupService';
import { getCurrencySymbol } from '../utils/currencyUtils';

const TicketsPage = () => {
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Tickets data
  const [tickets, setTickets] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Load tickets
  useEffect(() => {
    if (!adminGroup?.id) return;
    
    loadTicketsData(0, true);
  }, [adminGroup?.id]);

  const loadTicketsData = async (offset = 0, showLoadingState = false) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      
      console.log('Loading tickets for group:', adminGroup?.id);
      
      const params = {
        limit: pagination.limit,
        offset
      };
      
      const response = await groupService.getGroupTicketPurchases(adminGroup.id, params);
      console.log('Tickets API response:', response);
      
      const ticketsData = response?.data || [];
      
      console.log('Loaded tickets:', ticketsData.length, 'purchases');
      
      if (offset === 0) {
        setTickets(ticketsData);
      } else {
        setTickets(prev => [...prev, ...ticketsData]);
      }
      
      setPagination(response?.pagination || pagination);
      setError(null);
    } catch (err) {
      console.error('Error loading tickets:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load tickets');
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  const handleLoadMore = () => {
    if (!loading && pagination.hasMore) {
      loadTicketsData(pagination.offset + pagination.limit);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-range-dropdown')) {
        setShowDateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateDropdown]);

  // Get date range filter
  const getDateRangeFilter = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (range) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case 'last7days':
        return { start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), end: now };
      case 'last30days':
        return { start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), end: now };
      case 'last6months':
        return { start: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()), end: now };
      case 'thisyear':
        return { start: new Date(now.getFullYear(), 0, 1), end: now };
      default:
        return null;
    }
  };

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All time' },
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last30days', label: 'Last 30 days' },
    { value: 'last6months', label: 'Last 6 months' },
    { value: 'thisyear', label: 'This year' }
  ];

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const primaryName = `${ticket.primary_attendee_first_name || ''} ${ticket.primary_attendee_last_name || ''}`.trim();
      const eventTitle = ticket.events?.title || '';
      const confirmationNumber = ticket.confirmation_number || '';
      
      const matchesSearch = (
        primaryName.toLowerCase().includes(searchLower) ||
        eventTitle.toLowerCase().includes(searchLower) ||
        confirmationNumber.toLowerCase().includes(searchLower)
      );
      
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (dateRange !== 'all') {
      const dateFilter = getDateRangeFilter(dateRange);
      if (dateFilter) {
        const ticketDate = new Date(ticket.payment_completed_at || ticket.created_at);
        if (ticketDate < dateFilter.start || ticketDate > dateFilter.end) {
          return false;
        }
      }
    }
    
    return true;
  });

  // Sort tickets by date (newest first)
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const dateA = new Date(a.payment_completed_at || a.created_at);
    const dateB = new Date(b.payment_completed_at || b.created_at);
    return dateB - dateA;
  });

  // Calculate total revenue
  const totalRevenue = tickets.reduce((sum, ticket) => sum + (ticket.total_amount_cents || 0), 0) / 100;

  // Render summary box
  const renderSummaryBox = () => {
    const currencySymbol = getCurrencySymbol('usd'); // Most ticket purchases will be USD
    
    return (
      <div className="summary-boxes">
        <div className="summary-box">
          <div className="summary-content">
            <div className="summary-left">
              <div className="summary-label">
                <FontAwesomeIcon icon={faCheckCircle} className="status-icon succeeded" />
                Total Ticket Revenue
              </div>
              <div className="summary-value">
                {currencySymbol}{totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // CSV Export function
  const handleExportCSV = () => {
    if (tickets.length === 0) {
      alert('No tickets to export');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Date',
      'Confirmation Number',
      'Event',
      'Purchaser Name',
      'Purchaser Email',
      'Number of Tickets',
      'Price Per Ticket',
      'Total Amount',
      'Platform Fee',
      'Attendees'
    ];

    // Prepare CSV rows
    const csvRows = tickets.map(ticket => {
      const date = new Date(ticket.payment_completed_at || ticket.created_at).toLocaleDateString();
      const primaryName = `${ticket.primary_attendee_first_name || ''} ${ticket.primary_attendee_last_name || ''}`.trim() || 'Anonymous';
      const eventTitle = ticket.events?.title || 'Unknown Event';
      const pricePerTicket = `$${(ticket.price_per_ticket_cents / 100).toFixed(2)}`;
      const totalAmount = `$${(ticket.total_amount_cents / 100).toFixed(2)}`;
      const platformFee = `$${(ticket.platform_fee_cents / 100).toFixed(2)}`;
      
      // Parse attendee names
      let attendeeNames = '';
      if (ticket.attendee_names) {
        try {
          const attendees = typeof ticket.attendee_names === 'string' 
            ? JSON.parse(ticket.attendee_names) 
            : ticket.attendee_names;
          
          if (Array.isArray(attendees)) {
            attendeeNames = attendees.map(a => {
              if (typeof a === 'string') return a;
              if (a.name) return a.name;
              return '';
            }).filter(Boolean).join('; ');
          }
        } catch (e) {
          console.error('Error parsing attendee names:', e);
        }
      }
      
      // Escape commas and quotes in CSV data
      const escapeCSV = (field) => {
        if (field == null) return '';
        const str = String(field);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [
        escapeCSV(date),
        escapeCSV(ticket.confirmation_number || ''),
        escapeCSV(eventTitle),
        escapeCSV(primaryName),
        escapeCSV(''), // No separate email field in ticket purchases
        escapeCSV(ticket.attendee_count || 1),
        escapeCSV(ticket.attendee_count || 1),
        escapeCSV(pricePerTicket),
        escapeCSV(totalAmount),
        escapeCSV(platformFee),
        escapeCSV(attendeeNames)
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tickets-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render filters
  const renderFilters = () => (
    <div className="chip-filters-container">
      <div className="chip-filters">
        {/* No chip filters for tickets - just the search and date range */}
      </div>
      
      {/* Right side controls - Export and Date Picker */}
      <div className="right-controls">
        {/* Export Link */}
        <button
          className="export-link"
          onClick={handleExportCSV}
          title="Export tickets to CSV"
          disabled={tickets.length === 0}
        >
          <FontAwesomeIcon icon={faDownload} />
          <span>Export</span>
        </button>
        
        {/* Date Range Picker */}
        <div className="date-range-picker">
          <div className="date-range-dropdown">
            <button
              className={`date-range-button ${dateRange !== 'all' ? 'active' : ''}`}
              onClick={() => setShowDateDropdown(!showDateDropdown)}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              {dateRangeOptions.find(option => option.value === dateRange)?.label || 'All time'}
              <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
            </button>
            
            {showDateDropdown && (
              <div className="date-range-menu">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`date-range-option ${dateRange === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setDateRange(option.value);
                      setShowDateDropdown(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render tickets table
  const renderTicketsTable = () => {
    if (loading && tickets.length === 0) {
      return (
        <div className="loading-state">
          <p>Loading tickets...</p>
        </div>
      );
    }
    
    if (tickets.length === 0 && !loading) {
      return (
        <div className="empty-state">
          <h3>No ticket purchases yet</h3>
          <p>When members purchase tickets for your events, they will appear here.</p>
        </div>
      );
    }

    if (sortedTickets.length === 0 && tickets.length > 0) {
      return (
        <div className="empty-state">
          <h3>No matching tickets</h3>
          <p>Try adjusting your filters or search query.</p>
        </div>
      );
    }

    return (
      <TicketTable
        tickets={sortedTickets}
        onLoadMore={handleLoadMore}
        hasMore={pagination.hasMore}
        loading={loading}
      />
    );
  };

  return (
    <AdminLayout>
      <div className="financials-page">
        <div className="financials-container">
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-top">
              <h1>Tickets</h1>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="error-state" style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '24px',
              color: '#dc2626'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Summary Box */}
          {renderSummaryBox()}

          {/* Filters */}
          {renderFilters()}

          {/* Tickets Table */}
          {renderTicketsTable()}
        </div>
      </div>
    </AdminLayout>
  );
};

export default TicketsPage;
